import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon, 
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const Permissions = () => {
  const [menuPermissions, setMenuPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPermissionId, setEditingPermissionId] = useState(null);
  const [menuPath, setMenuPath] = useState('');
  const [menuName, setMenuName] = useState('');
  const [canAccess, setCanAccess] = useState(true);
  const [roleId, setRoleId] = useState('');

  // Fetch permissions and roles
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch roles first
        const rolesResponse = await api.get('/auth/roles');
        const rolesData = rolesResponse.data.roles || [];
        setRoles(rolesData);
        
        // If we have roles, fetch permissions for the first role by default
        if (rolesData.length > 0) {
          setSelectedRole(rolesData[0].id);
          await fetchPermissionsForRole(rolesData[0].id);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load permissions. Please try again later.');
        toast.error('Failed to load permissions data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch permissions for a specific role
  const fetchPermissionsForRole = async (roleId) => {
    try {
      setLoading(true);
      const response = await api.get(`/auth/roles/${roleId}/permissions`);
      setMenuPermissions(response.data.permissions || []);
      return response.data.permissions || [];
    } catch (err) {
      console.error(`Error fetching permissions for role ${roleId}:`, err);
      toast.error('Failed to load permissions for selected role');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle role selection change
  const handleRoleChange = async (e) => {
    const newRoleId = e.target.value;
    setSelectedRole(newRoleId);
    await fetchPermissionsForRole(newRoleId);
  };

  // Reset form state
  const resetForm = () => {
    setMenuPath('');
    setMenuName('');
    setCanAccess(true);
    setRoleId(selectedRole || '');
    setEditingPermissionId(null);
  };

  // Handle form submission for create/update
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!menuPath || !menuName || !roleId) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const permissionData = {
        menuPath,
        menuName,
        canAccess,
        roleId
      };
      
      let response;
      
      if (editingPermissionId) {
        // Update existing permission
        response = await api.put(`/auth/permissions/${editingPermissionId}`, permissionData);
        toast.success('Permission updated successfully');
        
        // Update the permissions list
        await fetchPermissionsForRole(selectedRole);
      } else {
        // Create new permission
        response = await api.post('/auth/permissions', permissionData);
        toast.success('Permission created successfully');
        
        // Refresh permissions list
        await fetchPermissionsForRole(selectedRole);
      }
      
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving permission:', err);
      toast.error(err.response?.data?.message || 'Failed to save permission');
    }
  };

  // Handle editing a permission
  const handleEdit = (permission) => {
    setEditingPermissionId(permission.id);
    setMenuPath(permission.menu_path);
    setMenuName(permission.menu_name);
    setCanAccess(permission.can_access);
    setRoleId(permission.role_id);
    setShowAddForm(true);
  };

  // Handle deleting a permission
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this permission?')) {
      return;
    }
    
    try {
      await api.delete(`/auth/permissions/${id}`);
      toast.success('Permission deleted successfully');
      
      // Refresh permissions list
      await fetchPermissionsForRole(selectedRole);
      
      // If we were editing this permission, cancel edit mode
      if (editingPermissionId === id) {
        setShowAddForm(false);
        resetForm();
      }
    } catch (err) {
      console.error('Error deleting permission:', err);
      toast.error('Failed to delete permission');
    }
  };

  // Handle toggling permission access
  const handleToggleAccess = async (permission) => {
    try {
      await api.put(`/auth/permissions/${permission.id}`, {
        ...permission,
        canAccess: !permission.can_access
      });
      toast.success(`Access ${!permission.can_access ? 'granted' : 'revoked'} successfully`);
      
      // Refresh permissions list
      await fetchPermissionsForRole(selectedRole);
    } catch (err) {
      console.error('Error toggling permission access:', err);
      toast.error('Failed to update permission');
    }
  };

  if (loading && menuPermissions.length === 0 && !selectedRole) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
      )}
      
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h2 className="text-lg font-medium text-gray-900">Menu Permissions</h2>
          <p className="mt-2 text-sm text-gray-700">
            Manage access to different sections of the application by role
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex gap-4">
          {/* Role selector */}
          <div>
            <label htmlFor="role-select" className="sr-only">Select Role</label>
            <select
              id="role-select"
              value={selectedRole || ''}
              onChange={handleRoleChange}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
            >
              {roles.map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>
          
          {/* Add button */}
          {!showAddForm && (
            <button
              onClick={() => {
                resetForm();
                setRoleId(selectedRole);
                setShowAddForm(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Add Permission
            </button>
          )}
        </div>
      </div>
      
      {/* Permission Form (Add or Edit) */}
      {showAddForm && (
        <div className="mt-6 bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              {editingPermissionId ? 'Edit Permission' : 'Add New Permission'}
            </h3>
            
            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div className="sm:col-span-3">
                  <label htmlFor="menu-path" className="block text-sm font-medium text-gray-700">
                    Menu Path <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="menu-path"
                      id="menu-path"
                      required
                      value={menuPath}
                      onChange={(e) => setMenuPath(e.target.value)}
                      placeholder="/path/to/page"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">For example: /security/users</p>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="menu-name" className="block text-sm font-medium text-gray-700">
                    Menu Name <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="menu-name"
                      id="menu-name"
                      required
                      value={menuName}
                      onChange={(e) => setMenuName(e.target.value)}
                      placeholder="Display name"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <label htmlFor="role-id" className="block text-sm font-medium text-gray-700">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <select
                      id="role-id"
                      name="role-id"
                      required
                      value={roleId}
                      onChange={(e) => setRoleId(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="">Select Role</option>
                      {roles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="sm:col-span-3">
                  <div className="flex items-center">
                    <input
                      id="can-access"
                      name="can-access"
                      type="checkbox"
                      checked={canAccess}
                      onChange={(e) => setCanAccess(e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="can-access" className="ml-2 block text-sm text-gray-900">
                      Allow Access
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    When checked, the role will have access to this menu path.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-5">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  className="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {editingPermissionId ? 'Update Permission' : 'Add Permission'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Permissions Table */}
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {!selectedRole ? (
                <div className="px-6 py-8 text-center text-gray-500 bg-white">
                  <p>Please select a role to view permissions.</p>
                </div>
              ) : menuPermissions.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500 bg-white">
                  <p>No permissions found for selected role. Click "Add Permission" to create a new permission.</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Menu Path
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Menu Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Access
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {menuPermissions.map((permission) => (
                      <tr key={permission.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {permission.menu_path}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {permission.menu_name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <button
                            onClick={() => handleToggleAccess(permission)}
                            className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                              permission.can_access
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                          >
                            {permission.can_access ? (
                              <>
                                <CheckIcon className="h-4 w-4 mr-1" />
                                Allowed
                              </>
                            ) : (
                              <>
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Denied
                              </>
                            )}
                          </button>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex items-center space-x-3 justify-end">
                            <button
                              onClick={() => handleEdit(permission)}
                              className="text-gray-500 hover:text-indigo-600"
                              title="Edit permission"
                            >
                              <PencilIcon className="h-5 w-5" aria-hidden="true" />
                              <span className="sr-only">Edit permission</span>
                            </button>
                            <button
                              onClick={() => handleDelete(permission.id)}
                              className="text-gray-500 hover:text-red-600"
                              title="Delete permission"
                            >
                              <TrashIcon className="h-5 w-5" aria-hidden="true" />
                              <span className="sr-only">Delete permission</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permissions; 